import * as React from 'react';
import { styled } from '@mui/material/styles';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { IsOpen, minutesOfDay } from '../../../../utils/time';
import { useState } from 'react';

const IsOpenTypography = styled(Typography)(() => ({
  display: 'flex',
  fontFamily: 'Raleway',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  alignItems: 'center',
  margin: '0 5px',
  color: '#3DC93B',
}));

const NowTypography = styled(Typography)(() => ({
  fontFamily: 'Raleway',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '15px',
  alignItems: 'center',
  color: '#000000',
}));

const CurrentTimeBox = styled(Box)(() => ({
  display: 'flex',
  fontFamily: 'Raleway',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '15px',
  alignItems: 'center',
  gap: '6px',
  color: '#000000',
}));

const OpenTimeCardContent = styled(CardContent)(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '-15px',
  gap: '10px',
}));

const OpenTimeTypography = styled(Typography)(() => ({
  width: '200px',
  fontFamily: 'Raleway',
  fontStyle: 'normal',
  fontWeight: '400',
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '14px',
  color: '#000000',
}));

const CloseSpan = styled('span')(() => ({
  color: 'red',
}));

const ResponsiveContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('850')]: {
    marginRight: '2px',
  },
  [theme.breakpoints.down('450')]: {
    marginRight: '82px',
  },
  [theme.breakpoints.down('370')]: {
    marginRight: '95px',
  },
}));

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const OpenTimeItem = (props) => {
  const openStatus = () => {
    if (props.isClosed === true) {
      return false;
    }
    return true;
  };
  const [spanText, setSpanText] = useState(openStatus());
  const displayText = props.openTime + ' - ' + props.closeTime;

  return (
    <OpenTimeTypography>
      {props.day}
      <span>{spanText ? displayText : <CloseSpan>Closed</CloseSpan>}</span>
    </OpenTimeTypography>
  );
};

const CafeIsOpen = ({ cafeData }) => {
  const hoursInfo = cafeData.tradingHours;
  const OpenTimeArray = [
    {
      day: '0',
      openTime: hoursInfo.Sun.openTime,
      closeTime: hoursInfo.Sun.closeTime,
      isClosed: hoursInfo.Sun.isClosed,
    },
    {
      day: '1',
      openTime: hoursInfo.Mon.openTime,
      closeTime: hoursInfo.Mon.closeTime,
      isClosed: hoursInfo.Mon.isClosed,
    },
    {
      day: '2',
      openTime: hoursInfo.Tue.openTime,
      closeTime: hoursInfo.Tue.closeTime,
      isClosed: hoursInfo.Tue.isClosed,
    },
    {
      day: '3',
      openTime: hoursInfo.Wed.openTime,
      closeTime: hoursInfo.Wed.closeTime,
      isClosed: hoursInfo.Wed.isClosed,
    },
    {
      day: '4',
      openTime: hoursInfo.Thu.openTime,
      closeTime: hoursInfo.Thu.closeTime,
      isClosed: hoursInfo.Thu.isClosed,
    },
    {
      day: '5',
      openTime: hoursInfo.Fri.openTime,
      closeTime: hoursInfo.Fri.closeTime,
      isClosed: hoursInfo.Fri.isClosed,
    },
    {
      day: '6',
      openTime: hoursInfo.Sat.openTime,
      closeTime: hoursInfo.Sat.closeTime,
      isClosed: hoursInfo.Sat.isClosed,
    },
  ];
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const tDate = new Date();
  const tDay = tDate.getDay();
  const tTime = tDate.getHours() + ':' + tDate.getMinutes();

  const [expanded, setExpanded] = React.useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const openNow = IsOpen(OpenTimeArray) && expanded && <NowTypography>Now</NowTypography>;
  const closed = expanded && !IsOpen(OpenTimeArray) && <span></span>;
  const openUntil = IsOpen(OpenTimeArray) && (
    <CurrentTimeBox>
      until {OpenTimeArray[tDay].closeTime} {weekday[tDay]}
    </CurrentTimeBox>
  );
  const closeUntilAm = minutesOfDay(tTime) < minutesOfDay(OpenTimeArray[tDay].openTime) && (
    <CurrentTimeBox>
      until {OpenTimeArray[tDay].openTime} {weekday[tDay]}
    </CurrentTimeBox>
  );
  const closeUntilPm = (
    <CurrentTimeBox>
      until {OpenTimeArray[tDay === 6 ? 0 : tDay + 1].openTime} {weekday[tDay === 6 ? 0 : tDay + 1]}
    </CurrentTimeBox>
  );

  return (
    <ResponsiveContainer>
      <CardActions sx={{ margin: 0, padding: 0 }}>
        <AccessTimeIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
        <IsOpenTypography>
          {IsOpen(OpenTimeArray) ? 'Open' : <CloseSpan>Closed</CloseSpan>}
        </IsOpenTypography>
        {openNow || closed || openUntil || closeUntilAm || closeUntilPm}
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <OpenTimeCardContent>
          {OpenTimeArray.map((item, index) => (
            <OpenTimeItem
              key={index}
              day={weekday[index]}
              openTime={item.openTime}
              closeTime={item.closeTime}
              isClosed={item.isClosed}
            />
          ))}
        </OpenTimeCardContent>
      </Collapse>
    </ResponsiveContainer>
  );
};
export default CafeIsOpen;
